import { useState, useEffect, useMemo } from "react";
import { useRoute, Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft,
  Target,
  Calendar as CalendarIcon,
  CheckSquare,
  BookOpen,
  Activity,
  Plus,
  ChevronDown,
  X,
  FileIcon,
  FileText,
  FileVideo,
  Play,
  Pause,
  GripVertical,
  Share2,
  BarChart2
} from "lucide-react";
import type { Goal, MediaFile, GoalMedia, UserPreferences } from "@shared/schema";
import GoalOverviewTab from "@/components/goal-detail/goal-overview-tab";
import GoalHabitsTab from "@/components/goal-detail/goal-habits-tab";
import GoalTasksTab from "@/components/goal-detail/goal-tasks-tab";
import GoalJournalTab from "@/components/goal-detail/goal-journal-tab";
import GoalCalendarTab from "@/components/goal-detail/goal-calendar-tab";
import { GoalTrackerXTab } from "@/components/goal-detail/goal-trackerx-tab";
import ShareDialog from "@/components/share-dialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { isVideoMediaFile, isImageMediaFile } from "@/components/attachments/inline-media";

type GoalMediaWithFile = GoalMedia & { media: MediaFile };

function SortableMediaItem({ item, onRemove }: { item: GoalMediaWithFile; onRemove: (id: string) => void }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isImage = isImageMediaFile(item.media);
  const isVideo = isVideoMediaFile(item.media);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative flex-shrink-0 group"
      data-testid={`media-item-${item.id}`}
    >
      <div className="relative w-48 h-48 bg-muted rounded-lg overflow-hidden border-2 border-border">
        {isVideo ? (
          <video
            src={item.media.fileUrl}
            controls
            preload="metadata"
            className="w-full h-full object-contain bg-black"
            data-testid={`video-attachment-${item.id}`}
          />
        ) : isImage ? (
          <img
            src={item.media.fileUrl}
            alt={item.media.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
            {item.media.fileType === 'pdf' ? (
              <FileText className="w-16 h-16 mb-2" />
            ) : item.media.fileType === 'mpeg' ? (
              <FileVideo className="w-16 h-16 mb-2" />
            ) : (
              <FileIcon className="w-16 h-16 mb-2" />
            )}
            <span className="text-sm text-center px-2">{item.media.name}</span>
          </div>
        )}
        <button
          className="absolute top-2 left-2 p-1 bg-background/90 rounded cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
          {...attributes}
          {...listeners}
          data-testid={`drag-handle-${item.id}`}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <button
          onClick={() => onRemove(item.id)}
          className="absolute top-2 right-2 p-1 bg-destructive text-destructive-foreground rounded hover:bg-destructive/90 opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`remove-media-${item.id}`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function MediaSelectorDialog({
  goalId,
  open,
  onOpenChange,
  onMediaAdded,
}: {
  goalId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMediaAdded?: () => void;
}) {
  const [selectedMediaIds, setSelectedMediaIds] = useState<string[]>([]);
  const { toast } = useToast();

  const { data: mediaFiles = [], isLoading: isLoadingMedia } = useQuery<MediaFile[]>({
    queryKey: ["/api/media"],
  });

  const addMediaMutation = useMutation({
    mutationFn: async (mediaId: string) => {
      return apiRequest('POST', `/api/goals/${goalId}/media`, { mediaId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/goals', goalId, 'media'] });
    },
    onError: () => {
      toast({ title: "Couldn't add media to goal", description: "Please try again.", variant: "destructive" });
    },
  });

  const handleAddMedia = async () => {
    try {
      for (const mediaId of selectedMediaIds) {
        await addMediaMutation.mutateAsync(mediaId);
      }
      setSelectedMediaIds([]);
      onOpenChange(false);
      onMediaAdded?.();
    } catch (error) {
      // Toast is shown by the mutation-level onError; keep the dialog open.
    }
  };

  const toggleMediaSelection = (mediaId: string) => {
    setSelectedMediaIds(prev =>
      prev.includes(mediaId)
        ? prev.filter(id => id !== mediaId)
        : [...prev, mediaId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Select Media Files</DialogTitle>
          <DialogDescription>
            Choose one or more media files to add to this goal
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          {isLoadingMedia ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-muted-foreground">Loading media files...</p>
            </div>
          ) : mediaFiles.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No media files available. Upload media files first in the Media page.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {mediaFiles.map((media) => {
                const isSelected = selectedMediaIds.includes(media.id);
                const isImage = ['jpeg', 'png', 'jpg'].includes(media.fileType.toLowerCase());
                
                return (
                  <div
                    key={media.id}
                    onClick={() => toggleMediaSelection(media.id)}
                    className={`relative cursor-pointer rounded-lg border-2 transition-all ${
                      isSelected ? 'border-primary ring-2 ring-primary' : 'border-border hover:border-primary/50'
                    }`}
                    data-testid={`media-selector-item-${media.id}`}
                  >
                    <div className="aspect-square bg-muted rounded-lg overflow-hidden">
                      {isImage ? (
                        <img
                          src={media.fileUrl}
                          alt={media.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
                          {media.fileType === 'pdf' ? (
                            <FileText className="w-12 h-12" />
                          ) : media.fileType === 'mpeg' ? (
                            <FileVideo className="w-12 h-12" />
                          ) : (
                            <FileIcon className="w-12 h-12" />
                          )}
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-sm font-medium truncate">{media.name}</p>
                      <p className="text-xs text-muted-foreground">{media.fileType.toUpperCase()}</p>
                    </div>
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <span className="text-xs text-primary-foreground">✓</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex justify-between items-center pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            {selectedMediaIds.length} file(s) selected
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel-media-selector">
              Cancel
            </Button>
            <Button
              onClick={handleAddMedia}
              disabled={selectedMediaIds.length === 0 || addMediaMutation.isPending}
              data-testid="button-add-selected-media"
            >
              {addMediaMutation.isPending ? 'Adding...' : `Add ${selectedMediaIds.length > 0 ? `(${selectedMediaIds.length})` : ''}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MediaCarousel({ goal }: { goal: Goal }) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(goal.mediaGalleryExpanded || false);
  
  // Sync with prop changes
  useEffect(() => {
    setIsOpen(goal.mediaGalleryExpanded || false);
  }, [goal.mediaGalleryExpanded]);
  const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false);
  const [slideshowMode, setSlideshowMode] = useState<'static' | 'slideshow'>(goal.mediaSlideshowMode as 'static' | 'slideshow' || 'static');
  const [slideshowInterval, setSlideshowInterval] = useState(goal.mediaSlideshowInterval || 3);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [localMedia, setLocalMedia] = useState<GoalMediaWithFile[]>([]);

  // Sync slideshow settings with database changes
  useEffect(() => {
    setSlideshowMode(goal.mediaSlideshowMode as 'static' | 'slideshow' || 'static');
    setSlideshowInterval(goal.mediaSlideshowInterval || 3);
  }, [goal.mediaSlideshowMode, goal.mediaSlideshowInterval]);

  const { data: goalMedia = [], isLoading } = useQuery<GoalMediaWithFile[]>({
    queryKey: ['/api/goals', goal.id, 'media'],
  });

  const updateMediaGalleryExpandedMutation = useMutation({
    mutationFn: async (expanded: boolean) => {
      return apiRequest('PATCH', `/api/goals/${goal.id}`, { mediaGalleryExpanded: expanded });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/goals', goal.id] });
    },
    onError: () => {
      toast({ title: "Couldn't save gallery setting", description: "Please try again.", variant: "destructive" });
    },
  });

  const updateSlideshowModeMutation = useMutation({
    mutationFn: async (mode: 'static' | 'slideshow') => {
      return apiRequest('PATCH', `/api/goals/${goal.id}`, { mediaSlideshowMode: mode });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/goals', goal.id] });
    },
    onError: () => {
      toast({ title: "Couldn't save slideshow setting", description: "Please try again.", variant: "destructive" });
    },
  });

  const updateSlideshowIntervalMutation = useMutation({
    mutationFn: async (interval: number) => {
      return apiRequest('PATCH', `/api/goals/${goal.id}`, { mediaSlideshowInterval: interval });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/goals', goal.id] });
    },
    onError: () => {
      toast({ title: "Couldn't save slideshow setting", description: "Please try again.", variant: "destructive" });
    },
  });

  const removeMediaMutation = useMutation({
    mutationFn: async (goalMediaId: string) => {
      return apiRequest('DELETE', `/api/goal-media/${goalMediaId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/goals', goal.id, 'media'] });
    },
    onError: () => {
      toast({ title: "Couldn't remove media", description: "Please try again.", variant: "destructive" });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async ({ id, displayOrder }: { id: string; displayOrder: number }) => {
      return apiRequest('PATCH', `/api/goal-media/${id}/order`, { displayOrder });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/goals', goal.id, 'media'] });
    },
    onError: () => {
      toast({ title: "Couldn't reorder media", description: "Please try again.", variant: "destructive" });
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setLocalMedia(goalMedia);
  }, [goalMedia]);

  const imageMedia = useMemo(() => 
    localMedia.filter(item => ['jpeg', 'png', 'jpg'].includes(item.media.fileType.toLowerCase())),
    [localMedia]
  );

  useEffect(() => {
    if (slideshowMode === 'slideshow' && imageMedia.length > 0) {
      const timer = setInterval(() => {
        setCurrentSlideIndex((prev) => (prev + 1) % imageMedia.length);
      }, slideshowInterval * 1000);
      return () => clearInterval(timer);
    }
    return undefined;
  }, [slideshowMode, slideshowInterval, imageMedia.length]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLocalMedia((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);

        newOrder.forEach((item, index) => {
          if (item.displayOrder !== index) {
            reorderMutation.mutate({ id: item.id, displayOrder: index });
          }
        });

        return newOrder;
      });
    }
  };

  const handleRemove = (goalMediaId: string) => {
    removeMediaMutation.mutate(goalMediaId);
  };

  const toggleSlideshowMode = () => {
    if (slideshowMode === 'static') {
      setSlideshowMode('slideshow');
      setCurrentSlideIndex(0);
    } else {
      setSlideshowMode('static');
      setCurrentSlideIndex(0);
    }
  };

  const handleToggleExpanded = (newState: boolean) => {
    setIsOpen(newState);
    // Only update database if different from current goal state
    if (newState !== goal.mediaGalleryExpanded) {
      updateMediaGalleryExpandedMutation.mutate(newState);
    }
  };

  const handleMediaAdded = () => {
    // Always expand when media is added
    setIsOpen(true);
    // Only update database if not already expanded
    if (!goal.mediaGalleryExpanded) {
      updateMediaGalleryExpandedMutation.mutate(true);
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={handleToggleExpanded} className="mb-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="p-0 hover:bg-transparent" data-testid="button-toggle-media-section">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg">Media Gallery</CardTitle>
                  <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
              </Button>
            </CollapsibleTrigger>
            <Button
              onClick={() => setIsMediaSelectorOpen(true)}
              size="sm"
              data-testid="button-add-media"
              className="min-h-[44px]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Media
            </Button>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-muted-foreground">Loading media...</p>
              </div>
            ) : localMedia.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No media added yet. Click "Add Media" to get started.
              </div>
            ) : (
              <>
                {imageMedia.length > 0 && (
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="slideshow-mode" className="text-sm">Display Mode:</Label>
                      <Select value={slideshowMode} onValueChange={(value: 'static' | 'slideshow') => {
                        setSlideshowMode(value);
                        updateSlideshowModeMutation.mutate(value);
                      }}>
                        <SelectTrigger className="w-32" id="slideshow-mode" data-testid="select-display-mode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="static">Static</SelectItem>
                          <SelectItem value="slideshow">Slideshow</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {slideshowMode === 'slideshow' && (
                      <div className="flex items-center gap-2">
                        <Label htmlFor="interval" className="text-sm">Interval (seconds):</Label>
                        <Select
                          value={slideshowInterval.toString()}
                          onValueChange={(value) => {
                            const interval = parseInt(value);
                            setSlideshowInterval(interval);
                            updateSlideshowIntervalMutation.mutate(interval);
                          }}
                        >
                          <SelectTrigger className="w-20" id="interval" data-testid="select-slideshow-interval">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="3">3s</SelectItem>
                            <SelectItem value="5">5s</SelectItem>
                            <SelectItem value="10">10s</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}
                
                {slideshowMode === 'slideshow' && imageMedia.length > 0 ? (
                  <div className="relative w-full">
                    <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                      <img
                        src={imageMedia[currentSlideIndex % imageMedia.length].media.fileUrl}
                        alt={imageMedia[currentSlideIndex % imageMedia.length].media.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex items-center justify-center gap-2 mt-4">
                      {imageMedia.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentSlideIndex(index)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === currentSlideIndex ? 'bg-primary' : 'bg-muted-foreground/30'
                          }`}
                          data-testid={`slideshow-indicator-${index}`}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={localMedia.map(item => item.id)}
                      strategy={horizontalListSortingStrategy}
                    >
                      <div className="flex gap-4 overflow-x-auto pb-4">
                        {localMedia.map((item) => (
                          <SortableMediaItem
                            key={item.id}
                            item={item}
                            onRemove={handleRemove}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
      <MediaSelectorDialog
        goalId={goal.id}
        open={isMediaSelectorOpen}
        onOpenChange={setIsMediaSelectorOpen}
        onMediaAdded={handleMediaAdded}
      />
    </Collapsible>
  );
}

const SMART_CRITERIA = [
  {
    key: 'smartSpecific' as const,
    letter: 'S',
    title: 'Specific',
    description: 'Is your goal clear and well-defined? Does it answer who, what, where, when, and why?'
  },
  {
    key: 'smartMeasurable' as const,
    letter: 'M',
    title: 'Measurable',
    description: 'Can you track your progress? Does it include specific metrics or milestones to measure success?'
  },
  {
    key: 'smartAchievable' as const,
    letter: 'A',
    title: 'Achievable',
    description: 'Is this goal realistic? Do you have the resources, skills, and time to accomplish it?'
  },
  {
    key: 'smartRelevant' as const,
    letter: 'R',
    title: 'Relevant',
    description: 'Does this goal align with your values and long-term objectives? Is it worth pursuing right now?'
  },
  {
    key: 'smartTimeBound' as const,
    letter: 'T',
    title: 'Time-Bound',
    description: 'Does your goal have a deadline? Is there a clear timeframe for achieving it?'
  }
];

function SmartGoalSection({ goal }: { goal: Goal }) {
  const { toast } = useToast();
  const { data: preferences } = useQuery<UserPreferences>({
    queryKey: ['/api/user/preferences']
  });
  
  const [isOpen, setIsOpen] = useState<boolean | null>(null);
  
  // Initialize isOpen from preferences once loaded
  useEffect(() => {
    if (preferences && isOpen === null) {
      setIsOpen(preferences.smartSectionExpanded !== false);
    }
  }, [preferences, isOpen]);

  const updatePreferenceMutation = useMutation({
    mutationFn: async (expanded: boolean) => {
      return apiRequest('PATCH', '/api/user/preferences', { smartSectionExpanded: expanded });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/preferences'] });
    },
    onError: () => {
      toast({ title: "Couldn't save section preference", description: "Please try again.", variant: "destructive" });
    },
  });

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    updatePreferenceMutation.mutate(open);
  };
  
  const updateSmartMutation = useMutation({
    mutationFn: async (updates: Partial<Goal>) => {
      return apiRequest('PATCH', `/api/goals/${goal.id}`, updates);
    },
    onMutate: async (updates) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['/api/goals', goal.id] });
      
      // Snapshot the previous value
      const previousGoal = queryClient.getQueryData<Goal>(['/api/goals', goal.id]);
      
      // Optimistically update the cache
      queryClient.setQueryData<Goal>(['/api/goals', goal.id], (old) => {
        if (!old) return old;
        return { ...old, ...updates };
      });
      
      return { previousGoal };
    },
    onError: (err, updates, context) => {
      // Rollback on error
      if (context?.previousGoal) {
        queryClient.setQueryData(['/api/goals', goal.id], context.previousGoal);
      }
      toast({ title: "Couldn't save SMART goal change", description: "Your change was not saved. Please try again.", variant: "destructive" });
    },
    onSettled: () => {
      // Refetch after mutation settles
      queryClient.invalidateQueries({ queryKey: ['/api/goals', goal.id] });
    }
  });

  const handleCheckboxChange = (key: string, checked: boolean) => {
    updateSmartMutation.mutate({ [key]: checked });
  };

  const handleBypassAll = () => {
    updateSmartMutation.mutate({ smartBypassed: true });
  };

  const allChecked = SMART_CRITERIA.every(c => goal[c.key]);
  const checkedCount = SMART_CRITERIA.filter(c => goal[c.key]).length;

  if (goal.smartBypassed) {
    return (
      <Card className="border-dashed border-muted-foreground/30" data-testid="card-smart-bypassed">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="h-5 w-5 text-muted-foreground" />
              S.M.A.R.T. Goal Qualification
            </CardTitle>
            <Badge variant="outline" className="text-muted-foreground">Bypassed</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            You chose to skip the S.M.A.R.T. qualification for this goal.
          </p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={() => updateSmartMutation.mutate({ smartBypassed: false })}
            data-testid="button-enable-smart"
          >
            Enable S.M.A.R.T. Qualification
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Collapsible open={isOpen ?? true} onOpenChange={handleOpenChange}>
      <Card data-testid="card-smart-qualification">
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-3 cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Is This Goal S.M.A.R.T. Qualified?
                </CardTitle>
                {allChecked && (
                  <Badge className="bg-green-500/10 text-green-700 dark:text-green-300">Complete</Badge>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">{checkedCount}/5</span>
                <ChevronDown className={`h-5 w-5 transition-transform ${(isOpen ?? true) ? 'rotate-180' : ''}`} />
              </div>
            </div>
            <CardDescription className="text-left">
              S.M.A.R.T. goals are Specific, Measurable, Achievable, Relevant, and Time-bound
            </CardDescription>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-4">
            {SMART_CRITERIA.map((criteria) => (
              <div
                key={criteria.key}
                className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                data-testid={`smart-item-${criteria.letter.toLowerCase()}`}
              >
                <Checkbox
                  id={criteria.key}
                  checked={goal[criteria.key] as boolean}
                  onCheckedChange={(checked) => handleCheckboxChange(criteria.key, checked as boolean)}
                  className="mt-1"
                  data-testid={`checkbox-smart-${criteria.letter.toLowerCase()}`}
                />
                <div className="flex-1">
                  <Label htmlFor={criteria.key} className="flex items-center gap-2 cursor-pointer">
                    <span className="font-bold text-primary text-lg">{criteria.letter}</span>
                    <span className="font-medium">{criteria.title}</span>
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {criteria.description}
                  </p>
                </div>
              </div>
            ))}
            
            <div className="flex items-center justify-between pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Not ready to evaluate? You can skip this for now.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBypassAll}
                data-testid="button-bypass-smart"
              >
                Bypass All
              </Button>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

export default function GoalDetailPage() {
  const [, params] = useRoute("/goals/:id");
  const goalId = params?.id;
  const [activeTab, setActiveTab] = useState("overview");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const { data: goal, isLoading } = useQuery<Goal>({
    queryKey: ["/api/goals", goalId],
    enabled: !!goalId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading goal details...</p>
        </div>
      </div>
    );
  }

  if (!goal) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <p className="text-muted-foreground">Goal not found</p>
          <Link href="/goals">
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Goals
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "low":
        return "bg-blue-500/10 text-blue-700 dark:text-blue-300";
      case "medium":
        return "bg-yellow-500/10 text-yellow-700 dark:text-yellow-300";
      case "high":
        return "bg-red-500/10 text-red-700 dark:text-red-300";
      default:
        return "bg-gray-500/10 text-gray-700 dark:text-gray-300";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-500/10 text-green-700 dark:text-green-300";
      case "completed":
        return "bg-blue-500/10 text-blue-700 dark:text-blue-300";
      case "paused":
        return "bg-gray-500/10 text-gray-700 dark:text-gray-300";
      default:
        return "bg-gray-500/10 text-gray-700 dark:text-gray-300";
    }
  };

  return (
    <>
      {/* Header */}
      <div className="bg-card border-b border-border px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-4">
              <Link href="/goals">
                <Button variant="ghost" size="sm" data-testid="button-back-to-goals">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Goals
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShareDialogOpen(true)}
                data-testid="button-share-goal"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-foreground flex items-center gap-2" data-testid="text-goal-title">
                  <Target className="h-6 w-6" />
                  {goal.title}
                </h1>
                {goal.description && (
                  <p className="text-muted-foreground mt-1" data-testid="text-goal-description">
                    {goal.description}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <Badge className={getStatusColor(goal.status)} data-testid="badge-status">
                    {goal.status}
                  </Badge>
                  <Badge className={getPriorityColor(goal.priority)} data-testid="badge-priority">
                    {goal.priority} Priority
                  </Badge>
                  {!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(goal.category || '') && goal.category && (
                    <Badge variant="outline" data-testid="badge-category">{goal.category}</Badge>
                  )}
                  {goal.deadline && (
                    <Badge variant="outline" className="gap-1" data-testid="badge-deadline">
                      <CalendarIcon className="w-3 h-3" />
                      Due: {new Date(goal.deadline).toLocaleDateString()}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="text-center">
                  <div className="text-3xl font-bold text-foreground" data-testid="text-progress-percentage">
                    {goal.progress}%
                  </div>
                  <div className="text-xs text-muted-foreground">Progress</div>
                </div>
                <Progress value={goal.progress} className="w-24 h-2" data-testid="progress-bar" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Media Carousel */}
      <div className="px-4 sm:px-6 lg:px-8 pt-6">
        <MediaCarousel goal={goal} />
      </div>

      {/* S.M.A.R.T. Goal Qualification */}
      <div className="px-4 sm:px-6 lg:px-8 pt-6">
        <SmartGoalSection goal={goal} />
      </div>

      {/* Tabs */}
      <div className="flex-1 px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 mb-6">
            <TabsTrigger value="overview" className="gap-2" data-testid="tab-overview">
              <Target className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="habits" className="gap-2" data-testid="tab-habits">
              <Activity className="w-4 h-4" />
              Habits
            </TabsTrigger>
            <TabsTrigger value="tasks" className="gap-2" data-testid="tab-tasks">
              <CheckSquare className="w-4 h-4" />
              Tasks
            </TabsTrigger>
            <TabsTrigger value="journal" className="gap-2" data-testid="tab-journal">
              <BookOpen className="w-4 h-4" />
              Journal
            </TabsTrigger>
            <TabsTrigger value="calendar" className="gap-2" data-testid="tab-calendar">
              <CalendarIcon className="w-4 h-4" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="trackerx" className="gap-2" data-testid="tab-trackerx">
              <BarChart2 className="w-4 h-4" />
              TrackerX
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <GoalOverviewTab goal={goal} />
          </TabsContent>

          <TabsContent value="habits">
            <GoalHabitsTab goalId={goal.id} />
          </TabsContent>

          <TabsContent value="tasks">
            <GoalTasksTab goalId={goal.id} />
          </TabsContent>

          <TabsContent value="journal">
            <GoalJournalTab goalId={goal.id} />
          </TabsContent>

          <TabsContent value="calendar">
            <GoalCalendarTab goalId={goal.id} />
          </TabsContent>

          <TabsContent value="trackerx">
            <GoalTrackerXTab goalId={goal.id} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Share Dialog */}
      {goal && (
        <ShareDialog
          isOpen={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          itemId={goal.id}
          itemType="goal"
          itemTitle={goal.title}
        />
      )}
    </>
  );
}
